import SensorStatusContainerStyle from "./css/SensorStatusContainer.module.css";
import SensorStatusFocus from "./SensorStatusFocus";
import { useState, useRef, useEffect } from "react";
import type { SensorStatusItemProps } from "../hooks/useSensorStatus";
import SensorStatusItem from "./SensorStatusItem";
import { useAppDispatch, useAppSelector } from "../../../shared/store/store";
import { setFilter, setFocusIndex, setIsStatusBoardOpen, setSelectedSensor, setAnimationOrigin, setShouldFitOnFilterChange } from "../slice/sensorDashboardSlice";
import useDevice from "../../../shared/hooks/useDevice";
import Flex from "../../../shared/components/Flex";
import Text from "../../../shared/components/Text";
import Seperator from "../../../shared/components/Seperator";
import Spacer from "../../../shared/components/Spacer";

type SensorStatusContainerProps = {
    items: SensorStatusItemProps[];
    immediate?: boolean;
    filled?: boolean;
    colorful?: boolean;
    noColor?: boolean;
}
function SensorStatusContainer({ items, immediate = false, filled = true, colorful = false, noColor = false }: SensorStatusContainerProps) {
    const focusIndex = useAppSelector(state => state.sensorDashboard.focusIndex);
    const currentFilter = useAppSelector(state => state.sensorDashboard.filter);
    const isStatusBoardOpen = useAppSelector(state => state.sensorDashboard.isStatusBoardOpen);
    const visibleMarkerCounts = useAppSelector(state => state.sensorDashboard.visibleMarkerCounts);
    const dispatch = useAppDispatch();
    const device = useDevice();
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    
    // 실제 마커 수를 사용하여 items 업데이트
    const itemsWithVisibleCounts = items.map(item => ({
        ...item,
        statusCnt: visibleMarkerCounts[item.codeName] ?? item.statusCnt
    }));
    
    // currentFilter가 변경될 때 해당 필터의 인덱스를 찾아서 focusIndex 동기화
    useEffect(() => {
        const index = itemsWithVisibleCounts.findIndex(item => item.codeName === currentFilter);
        if (index !== -1 && focusIndex !== index) {
            dispatch(setFocusIndex(index));
        }
    }, [currentFilter, itemsWithVisibleCounts, focusIndex, dispatch]);
    
    // 닫을 때 현재 탭의 위치 계산 함수
    const calculateOriginFromIndex = (index: number) => {
        if (device !== "desktop" || !itemRefs.current[index]) return;
        
        const element = itemRefs.current[index];
        if (!element) return;
        
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const boardLeft = 92;
        const boardTop = 12;
        const originX = centerX - boardLeft;
        const originY = centerY - boardTop;
        
        dispatch(setAnimationOrigin({ x: originX, y: originY }));
    };
    
    const handleClick = (index: number, codeName: string, event: React.MouseEvent<HTMLDivElement>) => {
        // 데스크탑에서만 애니메이션 origin 계산
        if (device === "desktop") {
            const clickedElement = event.currentTarget;
            const rect = clickedElement.getBoundingClientRect();
            // 클릭된 탭의 중심점 계산
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            
            // SensorStatusBoard의 최종 위치 (CSS에서 left: 92px, top: 12px)
            const boardLeft = 92;
            const boardTop = 12;
            
            // transform-origin을 위한 상대 좌표 계산 (보드의 왼쪽 상단 기준)
            const originX = centerX - boardLeft;
            const originY = centerY - boardTop;
            
            dispatch(setAnimationOrigin({ x: originX, y: originY }));
        }
        
        // 같은 카테고리를 다시 클릭하면 대시보드 토글
        if (currentFilter === codeName && isStatusBoardOpen) {
            // 닫을 때도 현재 탭의 위치에서 애니메이션
            if (device === "desktop") {
                const clickedElement = event.currentTarget;
                const rect = clickedElement.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                const boardLeft = 92;
                const boardTop = 12;
                const originX = centerX - boardLeft;
                const originY = centerY - boardTop;
                dispatch(setAnimationOrigin({ x: originX, y: originY }));
            }
            dispatch(setIsStatusBoardOpen(false));
            return;
        }
        
        // 다른 카테고리 클릭 시 해당 카테고리 대시보드 열기
        dispatch(setFocusIndex(index));
        dispatch(setFilter(codeName));
        dispatch(setIsStatusBoardOpen(true));
        dispatch(setSelectedSensor(null));
    }

    const handleFitAll = () => {
        dispatch(setShouldFitOnFilterChange(true));
        dispatch(setFilter("전체"));
        dispatch(setSelectedSensor(null));
    };
    return (
        <Flex flexDirection="column" gap={8} alignItems="center" justifyContent="space-between" fullHeight
            paddingTop={18}
            padding={8}
        >
            <Flex flexDirection="column" gap={16} alignItems="center" style={{ userSelect: "none" }}>
                <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={4}
                >
                        <img alt="" src="images/ico_geo_count.png"></img>
                        <Text size={"14px"} weight={"bold"}>Map</Text>
                </Flex>
                <div
                    style={{
                        backgroundColor: "#F5F7F9",
                        borderRadius: 8,
                        padding: "0 5px 5px",
                    }}
                >

                <div
                    className={SensorStatusContainerStyle.container}
                >
                    {
                        itemsWithVisibleCounts.map((item, index)=>(
                            <SensorStatusItem 
                                key={item.codeName} 
                                {...item} 
                                ref={(el) => { itemRefs.current[index] = el; }}
                                onClick={(e) => handleClick(index, item.codeName, e)}
                                data-tab-index={index}
                            />
                        ))
                    }
                    <SensorStatusFocus 
                        immediate={immediate}
                        filled={ false}
                        index={focusIndex}
                        itemAmount={itemsWithVisibleCounts.length}
                        color={noColor ? "transparent" : itemsWithVisibleCounts[focusIndex]?.color}
                        colorful={colorful}
                    />
                </div>
                </div>
                </Flex>
                <Flex
                flexDirection="column"
                gap={8}
                width={74}
                height={74}
                alignItems="center"
                justifyContent="center"
                style={{
                    width: "100%",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    border: "1px solid #DDDDDD",
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "transform 0.1s ease, box-shadow 0.1s ease",
                    marginBottom: 12,
                    userSelect: "none",
                    background: "#EAEFFF"
                }}
                onClick={handleFitAll}
                onMouseDown={(e) => {
                    const target = e.currentTarget;
                    target.style.background = "#B1BFF0";
                    dispatch(setIsStatusBoardOpen(false));
                }}
                onMouseUp={(e) => {
                    const target = e.currentTarget;
                    target.style.background = "#CCD6F8";
                }}
                onMouseEnter={(e) => {
                    const target = e.currentTarget;
                    target.style.background = "#CCD6F8";
                }}
                onMouseLeave={(e) => {
                    const target = e.currentTarget;
                    target.style.background = "#EAEFFF";
                }}
            >
                <img alt="" src="images/ico_map.png"></img>
                <Text size={"14px"} weight={"semibold"} color="#4C68C2">전체보기</Text>
            </Flex>
        </Flex>
    )
}

export default SensorStatusContainer;
